import type { Api } from '@rialtic/api'
import type { ClaimTypeHiro } from '@rialtic/types'
import type { PaAPI, RefDataModel } from '@rialtic/types'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type {
  FhirClaimLine,
  MappedLines,
  RawInsight,
  RawInsights,
} from '../types'
import { RefData } from '@rialtic/utils'

type RipClaimResponse = PaAPI.Get['/connector/{connector_id}/claim/{rid}']
type StateCode = PaAPI.Schemas['StateCode']

const { getMedicaidState } = RefData

function insertDotAfterThirdChar(input: string): string {
  if (input.length <= 3) {
    return input
  }
  return `${input.slice(0, 3)}.${input.slice(3)}`
}
interface ClaimLabState {
  autocompleteLoading: Set<string>
  autocompleteStore: Map<string, Set<string>>
  fhirData?: Api.ProcessedClaimWithInsights
  ripData?: RipClaimResponse
  workspaceId: string | undefined
  testResult: RawInsights | undefined
  newClaimLineSequences: Set<number>
  otherClaimLineSequence: Set<number>
}

const AutocompleteModel = readonly<RefDataModel[]>([
  'diagnosis',
  'placeofservice',
  'procedure',
  'typeofbill',
  'patientdischargestatuscode',
  'modifier',
  'revenue',
  'valuecode',
  'occurrencecode',
  'conditioncode',
  'specialty',
])

export const useClaimLab = defineStore('claim-lab', {
  state: (): ClaimLabState => {
    return {
      autocompleteLoading: new Set(),
      autocompleteStore: new Map(),
      fhirData: undefined,
      workspaceId: undefined,
      testResult: undefined,
      newClaimLineSequences: new Set(),
      otherClaimLineSequence: new Set(),
      ripData: undefined,
    }
  },

  getters: {
    /** This is an internal feature for Rialtic users only */
    isAllInsightsEnabled(): boolean {
      const isRialticUser = useIsRialticUser()

      return isRialticUser.value
    },
    claimLinesCount(): number {
      return this.fhirData?.claimLines.length || 0
    },
    testResultsBySequence(state): Map<number, RawInsights> | null {
      return state.testResult
        ? state.testResult.reduce((results, current) => {
            if (
              !this.isAllInsightsEnabled ||
              !Object.hasOwn(current, 'is_actionable_insight') ||
              current.is_actionable_insight
            ) {
              const sequence = current.insight.insight.claimLineSequenceNum

              if (!results.has(sequence)) results.set(sequence, [current])
              else results.get(sequence)?.push(current)
            }

            return results
          }, new Map<number, RawInsights>())
        : null
    },
    nonActionableInsightsBySequence(state): Map<number, RawInsights> | null {
      return this.isAllInsightsEnabled && state.testResult
        ? state.testResult.reduce((results, current) => {
            if (!current.is_actionable_insight) {
              const sequence = current.insight.insight.claimLineSequenceNum

              if (!results.has(sequence)) results.set(sequence, [current])
              else results.get(sequence)?.push(current)
            }

            return results
          }, new Map<number, RawInsights>())
        : null
    },
    influencingClaimLines(): Map<string, Set<number>> {
      return this.testResultsBySequence
        ? [...this.testResultsBySequence.values()].reduce(
            (acc, testResults) => {
              for (const result of testResults) {
                for (const info of result.insight.insight.historyInfo || []) {
                  for (const line of info.lines || []) {
                    if (!line.exit) {
                      acc.set(
                        info.claimId,
                        (acc.get(info.claimId) || new Set()).add(line.seq),
                      )
                    }
                  }
                }
              }

              return acc
            },
            new Map(),
          )
        : new Map()
    },
    totalClaimLines(state): number {
      const maxNewSequence =
        state.newClaimLineSequences.size > 0
          ? Math.max(...Array.from(state.newClaimLineSequences))
          : 0
      return Math.max(maxNewSequence, this.claimLinesCount)
    },

    getAutocompleteItems(state) {
      return (model: RefDataModel) => {
        return Array.from(
          state.autocompleteStore.get(model.toLowerCase()) || [],
        )
          .sort((a, b) => a.localeCompare(b))
          .map((item) => {
            return {
              value: item,
            }
          })
      }
    },

    isAutocompleteLoading(state) {
      return (model: RefDataModel) =>
        state.autocompleteLoading.has(model.toLowerCase())
    },

    claimType(state): ClaimTypeHiro {
      return state.fhirData?.type as ClaimTypeHiro
    },

    fhirClaimLines(state): MappedLines {
      return state.fhirData?.claimLines?.length
        ? state.fhirData.claimLines.reduce((lines, current) => {
            const raw_insights = state.fhirData?.insights
              ? state.fhirData.insights.reduce((accumulator, insight) => {
                  if (
                    // @ts-expect-error - `ProcessedClaimWithInsights` type doesn't seem to be correct, this is valid
                    insight.insight.insight.claimLineSequenceNum ===
                    current.sequence
                  )
                    accumulator.push(insight as RawInsight)

                  return accumulator
                }, [] as RawInsights)
              : ([] as RawInsights)
            lines.set(current.sequence, {
              ...current,
              raw_insights,
            })

            return lines
          }, new Map<number, FhirClaimLine>() as MappedLines)
        : (new Map<number, FhirClaimLine>() as MappedLines)
    },

    medicaidState(state): StateCode | null {
      const claimLabState = useClaimLabState()

      return getMedicaidState(
        state.fhirData?.additionalDetails?.payorShort ?? '',
        claimLabState.value.connectorName ?? '',
      )
    },
  },

  actions: {
    async fetchClaimData() {
      const route = useRoute()

      const { params } = route
      const connectorId =
        'connectorId' in params ? params.connectorId : undefined
      const claimId = 'claimId' in params ? params.claimId : undefined
      const rid = 'rid' in params ? params.rid : undefined

      const fhirData = await $apiFetch<Api.ProcessedClaimWithInsights>(
        `/api/claim-lab/connector/${connectorId}/claim/${claimId}`,
        {
          query: { rid },
        },
      )

      if (!fhirData) return

      this.fhirData = fhirData

      const headers: HeadersInit = this.workspaceId
        ? {
            'x-rialtic-workspace-id': this.workspaceId,
          }
        : {}

      const ripData = await $apiFetch<RipClaimResponse>(
        `/api/claim-lab/connector/${connectorId}/claim/${claimId}/rid/${rid}`,
        {
          headers,
        },
      )

      if (!ripData) return

      this.ripData = ripData

      this.processRefDataFromRip(ripData.claim.rip)
    },

    processRefDataFromRip({ lines }: any = {}) {
      if (!lines) return
      for (const line of lines) {
        if (line.procedure_code) {
          this.updateAutocompleteStore('procedure', [line.procedure_code])
        }
        if (line.place_of_service) {
          this.updateAutocompleteStore('placeofservice', [
            line.place_of_service,
          ])
        }

        const modifiers = [
          line.modifier_1,
          line.modifier_2,
          line.modifier_3,
          line.modifier_4,
        ]
        this.updateAutocompleteStore('modifier', modifiers)

        const diagnosis = [
          line.diagnosis_code_1,
          line.diagnosis_code_2,
          line.diagnosis_code_3,
          line.diagnosis_code_4,
        ]
        this.updateAutocompleteStore('diagnosis', diagnosis)
      }
    },

    resetClaimLab() {
      this.$patch({
        testResult: undefined,
        newClaimLineSequences: new Set(),
        fhirData: undefined,
        ripData: undefined,
      })
    },
    setTestResult(data: RawInsights | undefined) {
      this.testResult = data
    },
    addNewClaimLineSequence(sequence: number) {
      this.newClaimLineSequences.add(sequence)
    },
    removeNewClaimLineSequence(sequence: number) {
      this.newClaimLineSequences.delete(sequence)
    },
    addOtherClaimLineSequence(sequence: number) {
      this.otherClaimLineSequence.add(sequence)
    },
    removeOtherClaimLineSequence(sequence: number) {
      this.otherClaimLineSequence.delete(sequence)
    },
    setWorkspaceId(workspaceId: string) {
      this.workspaceId = workspaceId
    },

    async searchAutocompleteStore(model: RefDataModel, term: string) {
      const $model = model.toLowerCase() as RefDataModel

      if (!AutocompleteModel.includes($model)) {
        return
      }
      this.autocompleteLoading.add($model)
      try {
        const results = await $apiFetch<{ id: string; code: string }[]>(
          `/api/claim-lab/ref-data/${$model}`,
          {
            params: {
              search_phrase: term,
              requireSearch: true,
            },
          },
        )

        if (!results.length) {
          this.autocompleteLoading.delete($model)
          return
        }

        switch ($model) {
          case 'diagnosis':
            return this.updateAutocompleteStore(
              $model,
              results.map((r) => insertDotAfterThirdChar(r.code)),
            )
          case 'specialty':
            return this.updateAutocompleteStore(
              $model,
              results.map((r) => r.id),
            )
          case 'modifier':
          case 'revenue':
          case 'patientdischargestatuscode':
          case 'placeofservice':
          case 'typeofbill':
          case 'procedure':
          case 'valuecode':
          case 'occurrencecode':
          case 'conditioncode':
            return this.updateAutocompleteStore(
              $model,
              results.map((r) => r.code),
            )
          default:
            break
        }
        // TODO: Handle this error
        // oxlint-disable-next-line no-unused-vars
      } catch {
      } finally {
        this.autocompleteLoading.delete($model)
      }
    },

    updateAutocompleteStore(model: RefDataModel, items: string[]) {
      const $model = model.toLowerCase() as RefDataModel

      if (!Array.isArray(items) || !items.length) {
        return
      }

      const currentItems = this.autocompleteStore.get($model)

      if (!currentItems) {
        this.autocompleteStore.set($model, new Set(items.filter(Boolean)))
        return
      }

      for (const item of items) {
        if (item && !currentItems.has(item)) {
          currentItems.add(item)
        }
      }
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useClaimLab, import.meta.hot))
